import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimWorkOrderChangeObjectData, BimWorkOrderChangeOtherSelectionData } from 'gql/graphql';
import { useTableCollapse } from 'hooks/useTableCollapse';
import { useAddOtherToBimWorkOrderMenu } from 'pages/Project/BimProject/BimCommonComponents/BimAddOtherModal';
import { getBaseColumnsByContractType } from 'pages/Project/BimProject/BimCommonComponents/BimAddOtherModal/baseColumns';
import { getInitialChecked } from 'pages/Project/UerProject/WorkOrder/AddOrderOther/AddOthersTable';
import type { SelectedFilters, TableFilters } from 'uikit';
import { TableTextFilter } from 'uikit';

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderChangeOtherSelectionListGql } from '../bimWorkOrderChangeUploadOtherQueries';

type TAddToWorkOrderChange = {
    workOrderChangeId: number;
    constructionTypeId: number | undefined;
    tableRef: RefObject<HTMLDivElement>;
    workOrderObjects: BimWorkOrderChangeObjectData[];
};

const requestId = 'othersToBimWorkOrderChangeData';

export const useAddOtherToBimWorkOrderChangeTableData = (props: TAddToWorkOrderChange) => {
    const dispatch = useDispatch();
    const { tableRef, workOrderChangeId, constructionTypeId, workOrderObjects } = props;
    const isWorkOrderChangePage = !!workOrderChangeId;

    const [createdOtherId, setCreatedOtherId] = useState<number | null>(null);
    const [where, setWhere] = useState<SelectedFilters | undefined>(undefined);
    const onFiltersUpdated = useCallback((selectedFilters: SelectedFilters) => {
        setWhere(selectedFilters);
    }, []);

    const getSelectionListVariables = useMemo(() => {
        if (workOrderChangeId && constructionTypeId) {
            return {
                workOrderChangeId,
                constructionTypeId,
                where,
            };
        }

        return undefined;
    }, [constructionTypeId, workOrderChangeId, where]);

    const [data, status, , request] = useRequest({
        requestId,
        query: bimWorkOrderChangeOtherSelectionListGql,
        variables: getSelectionListVariables,
        withEffect: Boolean(getSelectionListVariables),
    });

    const [resetOthersData] = useResetStoreData({
        requestId,
    });

    const otherListWithConstructions = useMemo(
        () =>
            data?.bimWorkOrderChangeOtherSelectionList?.items?.map((other) => ({
                ...other,
                rows: workOrderObjects.map((object) => ({
                    ...object,
                    id: `${other.contractOtherCostId}_${object.id}`,
                    objectId: `${object.workOrderObjectId}_${object.workOrderChangeObjectId}`,
                    parentId: other.contractOtherCostId,
                })),
                id: other.contractOtherCostId,
            })) ?? [],
        [data?.bimWorkOrderChangeOtherSelectionList?.items, workOrderObjects],
    );

    const { menuColumn, checkedIds, setCheckedIds, setValueForCheckedRow, handleChangeCheckbox } =
        useAddOtherToBimWorkOrderMenu({
            othersWithConstructionTypes: otherListWithConstructions as any,
            disableCheckboxTooltip: false,
        });

    const baseColumnsByContractType = useMemo(
        () =>
            getBaseColumnsByContractType<BimWorkOrderChangeOtherSelectionData>({
                tableRef,
                checkedIds,
                valueSetter: setValueForCheckedRow,
                isWorkOrderChangePage,
            }),
        [tableRef, checkedIds, setValueForCheckedRow, isWorkOrderChangePage],
    );

    const [preparedColumns, preparedRows, , collapseAll] = useTableCollapse(
        'addOtherToBimWorkOrderChange',
        baseColumnsByContractType,
        otherListWithConstructions,
        undefined,
    );

    const finaleRowsAfterFilter = useMemo(() => {
        const passedIds: (string | number)[] = [];
        const withFilter = where?.name.selected.includes('name') && where?.name.value;

        return withFilter
            ? preparedRows.filter((row) => {
                  const isPassedFilters = row.parentId
                      ? passedIds.includes(row.parentId)
                      : where?.name.value.findIndex(
                            ({ value }: { value: string; name: string }) => row.name === value,
                        ) !== -1;

                  if (isPassedFilters && typeof row.contractOtherCostId !== 'undefined') {
                      passedIds.push(row.contractOtherCostId);
                  }

                  return isPassedFilters;
              })
            : preparedRows;
    }, [where?.name.selected, where?.name.value, preparedRows]);

    const finalColumns = useMemo<any[]>(() => [menuColumn, ...preparedColumns], [menuColumn, preparedColumns]);

    /*
     *ставим все чекбоксы в false для корректной работы тултипов и закрываем коллапсы
     */
    useEffect(() => {
        if (otherListWithConstructions.length && !Object.keys(checkedIds).length) {
            const initialChecked = !!otherListWithConstructions && getInitialChecked(otherListWithConstructions);

            setCheckedIds(initialChecked);
            collapseAll()();
        }
    }, [checkedIds, collapseAll, otherListWithConstructions, preparedRows, setCheckedIds]);

    useEffect(
        () => () => {
            resetOthersData(undefined);
        },
        [resetOthersData],
    );

    const tableFilters = useMemo<TableFilters<(typeof preparedRows)[number]>>(
        () => [
            {
                id: 'name',
                name: 'Наименование: ',
                field: 'name',
                filterer: () => true,
                component: {
                    name: TableTextFilter,
                    props: {
                        withSearch: true,
                        items: otherListWithConstructions.map((item: { name: string }) => ({
                            name: item.name,
                            value: item.name,
                        })),
                    },
                },
            },
        ],
        [otherListWithConstructions],
    );

    const haveCheckedOthers = Object.values(checkedIds).some((checkedId) => checkedId.parent);

    const refreshAddTableDataWithSelect = useCallback(
        (id: number) => {
            setCreatedOtherId(id);
            dispatch(request(getSelectionListVariables));
        },
        [dispatch, getSelectionListVariables, request],
    );

    const isDataLoaded = useMemo(() => status === RequestStatus.SUCCESS, [status]);

    useEffect(() => {
        if (isDataLoaded && createdOtherId) {
            const row = preparedRows.find((d) => d.contractOtherCostId === createdOtherId);

            if (row) {
                handleChangeCheckbox(row as any)({ checked: true });
                setCreatedOtherId(null);
            }
        }
    }, [createdOtherId, handleChangeCheckbox, isDataLoaded, preparedRows]);

    return {
        tableRows: finaleRowsAfterFilter,
        tableColumns: finalColumns,
        getDataStatus: status,
        onFiltersUpdated,
        tableFilters,
        haveCheckedOthers,
        checkedIds,
        refreshAddTableDataWithSelect,
    };
};
